// banking/src/pages/onboarding/corporate/steps/Step5_RepresentativeForm.tsx

import React, { useEffect, useState } from 'react';
import { getTranslation, type Language } from '../corporate-translations';
import type { CorporateFormData, Representative } from '../CorporateOnboarding';
import CountrySelector from '../components/CountrySelector';
import SOCSelector from '../components/SOCSelector';
import USStateSelector from '../components/USStateSelector';
import { PHONE_COUNTRY_CODES } from '../utils/phoneCountryCodes';
import { getCurrentApiUrl } from '../../../../config/server.config';

interface Step5Props {
  formData: CorporateFormData;
  updateFormData: (data: Partial<CorporateFormData>) => void;
  language: Language;
  errors: Record<string, string>;
  onNext: () => void; // Callback to advance to next step
}

const Step5_RepresentativeForm: React.FC<Step5Props> = ({
  formData,
  updateFormData,
  language,
  errors,
  onNext,
}) => {
  const currentIndex = formData.currentRepresentativeIndex;
  const isEditing = currentIndex >= 0 && currentIndex < formData.representatives.length;

  // Local errors state for representative form validation
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const [currentRep, setCurrentRep] = useState<Representative>(
    isEditing ? { ...formData.representatives[currentIndex] } : {
      relationshipType: '',
      role: '',
      isUltimateBeneficiary: true, // ← DEFAULT: true (checked by default)
      ownershipPercentage: 0,
      representationStartDate: new Date().toISOString().split('T')[0],
      firstName: '',
      lastName: '',
      middleName: '',
      secondLastName: '',
      gender: '',
      birthDate: '',
      birthCountry: '',
      nationality: '',
      citizenship: '',
      residenceCountry: '',
      documentType: '',
      documentNumber: '',
      documentCountry: '',
      documentExpiry: '',
      email: '',
      phoneCountryCode: '',
      phoneNumber: '',
      addressLine1: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      employmentStatus: '',
      occupation: '',
      employerName: '',
      jobTitle: '',
      taxCountry: '',
      taxNumber: '',
      isPep: false, // Default: No es PEP
      documents: {},
    }
  );

  useEffect(() => {
    if (isEditing) {
      // Load existing representative data
      setCurrentRep({ ...formData.representatives[currentIndex] });
    } else {
      // Reset to empty form for new representative
      setCurrentRep({
        relationshipType: '',
        role: '',
        isUltimateBeneficiary: true,
        ownershipPercentage: 0,
        representationStartDate: new Date().toISOString().split('T')[0],
        firstName: '',
        lastName: '',
        middleName: '',
        secondLastName: '',
        gender: '',
        birthDate: '',
        birthCountry: '',
        nationality: '',
        citizenship: '',
        residenceCountry: '',
        documentType: '',
        documentNumber: '',
        documentCountry: '',
        documentExpiry: '',
        email: '',
        phoneCountryCode: '',
        phoneNumber: '',
        addressLine1: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        employmentStatus: '',
        occupation: '',
        employerName: '',
        jobTitle: '',
        taxCountry: '',
        taxNumber: '',
        isPep: false,
        documents: {},
      });
    }
  }, [currentIndex, isEditing]);

  // NO AUTO-SAVE - Only save when user clicks "Next"
  const saveToFormData = () => {
    const newReps = [...formData.representatives];

    if (isEditing) {
      // Editing existing representative
      newReps[currentIndex] = currentRep;
    } else {
      // Adding new representative
      newReps.push(currentRep);
    }

    // Update form data
    updateFormData({
      representatives: newReps
    });
  };

  const handleSave = async () => {
    // Validate required fields - individual error messages per field
    const newErrors: Record<string, string> = {};

    // Basic Info
    if (!currentRep.relationshipType) newErrors.relationshipType = getTranslation('required_field', language);
    if (!currentRep.role) newErrors.role = getTranslation('required_field', language);

    // Ownership Percentage - MUST be > 0
    if (!currentRep.ownershipPercentage || currentRep.ownershipPercentage <= 0) {
      newErrors.ownershipPercentage = language === 'es'
        ? 'El porcentaje de propiedad debe ser mayor a 0'
        : language === 'fr'
        ? 'Le pourcentage de propriété doit être supérieur à 0'
        : language === 'de'
        ? 'Der Eigentumsanteil muss größer als 0 sein'
        : language === 'it'
        ? 'La percentuale di proprietà deve essere maggiore di 0'
        : language === 'pt'
        ? 'A percentagem de propriedade deve ser superior a 0'
        : language === 'nl'
        ? 'Het eigendomspercentage moet groter zijn dan 0'
        : language === 'ru'
        ? 'Процент владения должен быть больше 0'
        : language === 'ar'
        ? 'يجب أن تكون نسبة الملكية أكبر من 0'
        : 'Ownership percentage must be greater than 0';
    }

    // Personal Data
    if (!currentRep.firstName) newErrors.firstName = getTranslation('required_field', language);
    if (!currentRep.lastName) newErrors.lastName = getTranslation('required_field', language);
    if (!currentRep.gender) newErrors.gender = getTranslation('required_field', language);
    if (!currentRep.birthDate) newErrors.birthDate = getTranslation('required_field', language);
    if (!currentRep.birthCountry) newErrors.birthCountry = getTranslation('required_field', language);
    if (!currentRep.nationality) newErrors.nationality = getTranslation('required_field', language);
    if (!currentRep.citizenship) newErrors.citizenship = getTranslation('required_field', language);
    if (!currentRep.residenceCountry) newErrors.residenceCountry = getTranslation('required_field', language);

    // Document
    if (!currentRep.documentType) newErrors.documentType = getTranslation('required_field', language);
    if (!currentRep.documentNumber) newErrors.documentNumber = getTranslation('required_field', language);
    if (!currentRep.documentCountry) newErrors.documentCountry = getTranslation('required_field', language);
    if (!currentRep.documentExpiry) newErrors.documentExpiry = getTranslation('required_field', language);

    // Contact
    if (!currentRep.email) {
      newErrors.email = getTranslation('required_field', language);
    } else if (!/\S+@\S+\.\S+/.test(currentRep.email)) {
      newErrors.email = getTranslation('invalid_email', language);
    }
    if (!currentRep.phoneCountryCode) newErrors.phoneCountryCode = getTranslation('required_field', language);
    if (!currentRep.phoneNumber) newErrors.phoneNumber = getTranslation('required_field', language);

    // Address
    if (!currentRep.addressLine1) newErrors.addressLine1 = getTranslation('required_field', language);
    if (!currentRep.city) newErrors.city = getTranslation('required_field', language);
    if (!currentRep.state) newErrors.state = getTranslation('required_field', language);
    if (!currentRep.postalCode) newErrors.postalCode = getTranslation('required_field', language);
    if (!currentRep.country) newErrors.country = getTranslation('required_field', language);

    // Employment/Tax
    if (!currentRep.employmentStatus) newErrors.employmentStatus = getTranslation('required_field', language);
    if (!currentRep.occupation) newErrors.occupation = getTranslation('required_field', language);
    if (!currentRep.taxCountry) newErrors.taxCountry = getTranslation('required_field', language);
    if (!currentRep.taxNumber) newErrors.taxNumber = getTranslation('required_field', language);

    // Employer name and job title are only required if employed or self-employed
    if (currentRep.employmentStatus === 'EMPLOYED' || currentRep.employmentStatus === 'SELF_EMPLOYED') {
      if (!currentRep.employerName) newErrors.employerName = getTranslation('required_field', language);
      if (!currentRep.jobTitle) newErrors.jobTitle = getTranslation('required_field', language);
    }

    // If there are errors, set them and stop
    if (Object.keys(newErrors).length > 0) {
      setLocalErrors(newErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(newErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Clear errors if validation passes
    setLocalErrors({});

    const newReps = [...formData.representatives];
    let savedIndex = currentIndex;

    if (isEditing) {
      // Editing existing representative
      newReps[currentIndex] = currentRep;
    } else {
      // Adding new representative
      newReps.push(currentRep);
      savedIndex = newReps.length - 1; // Index of newly added rep
    }

    // Create updated formData with new representative
    const updatedFormData = {
      ...formData,
      representatives: newReps,
      currentRepresentativeIndex: savedIndex
    };

    // Update local state
    updateFormData({
      representatives: newReps,
      currentRepresentativeIndex: savedIndex
    });

    // Save to database immediately with updated data
    console.log('💾 Saving representative to database...');
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch(`${getCurrentApiUrl()}/onboarding/corporate/save-step`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            currentStep: 5,
            formData: updatedFormData
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Representative saved to database');

          // 🔑 CRITICAL: Update representative with ID from backend
          if (result.data?.representativeId && !isEditing) {
            console.log('🆔 Updating representative with ID:', result.data.representativeId);
            newReps[savedIndex] = { ...newReps[savedIndex], id: result.data.representativeId };
            updateFormData({
              representatives: newReps
            });
          }
        } else {
          console.error('❌ Failed to save representative to database');
        }
      }
    } catch (error) {
      console.error('Error saving representative:', error);
    }

    // Advance to Step 6 (documents)
    onNext();
  };

  const handleCancel = () => {
    updateFormData({ currentRepresentativeIndex: -1 });
  };

  const updateCurrentRep = (updates: Partial<Representative>) => {
    setCurrentRep(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {getTranslation('step5_title', language)}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {isEditing
            ? `${language === 'es' ? 'Editando' : language === 'fr' ? 'Modification' : language === 'de' ? 'Bearbeiten' : language === 'it' ? 'Modifica' : language === 'pt' ? 'Editando' : language === 'nl' ? 'Bewerken' : language === 'ru' ? 'Редактирование' : language === 'ar' ? 'تحرير' : 'Editing'}: ${currentRep.firstName} ${currentRep.lastName}`
            : getTranslation('step5_subtitle', language)
          }
        </p>
      </div>

      {/* Section 1: Relationship */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {language === 'es' ? '1. Relación con la Empresa' :
           language === 'fr' ? '1. Relation avec l\'entreprise' :
           language === 'de' ? '1. Beziehung zum Unternehmen' :
           language === 'it' ? '1. Relazione con l\'azienda' :
           language === 'pt' ? '1. Relação com a empresa' :
           language === 'nl' ? '1. Relatie met het bedrijf' :
           language === 'ru' ? '1. Отношение к компании' :
           language === 'ar' ? '1. العلاقة مع الشركة' :
           '1. Relationship with Company'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('relationship_type_label', language)} <span className="text-red-500">*</span>
            </label>
            <select
              name="relationshipType"
              value={currentRep.relationshipType}
              onChange={(e) => updateCurrentRep({ relationshipType: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.relationshipType ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">{getTranslation('select_option', language)}</option>
              <option value="DIRECTOR">{getTranslation('relationship_director', language)}</option>
              <option value="SHAREHOLDER">{getTranslation('relationship_shareholder', language)}</option>
              <option value="BENEFICIAL_OWNER">{getTranslation('relationship_beneficial_owner', language)}</option>
            </select>
            {localErrors.relationshipType && (
              <p className="mt-1 text-sm text-red-600">{localErrors.relationshipType}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('role_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              name="role"
              type="text"
              value={currentRep.role}
              onChange={(e) => updateCurrentRep({ role: e.target.value })}
              placeholder={getTranslation('role_placeholder', language)}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.role ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.role && (
              <p className="mt-1 text-sm text-red-600">{localErrors.role}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('ownership_percentage_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="ownershipPercentage"
              min="0.01"
              max="100"
              step="0.01"
              value={currentRep.ownershipPercentage}
              onChange={(e) => updateCurrentRep({ ownershipPercentage: parseFloat(e.target.value) || 0 })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.ownershipPercentage ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.ownershipPercentage && (
              <p className="mt-1 text-sm text-red-600">{localErrors.ownershipPercentage}</p>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isUltimateBeneficiary"
              checked={currentRep.isUltimateBeneficiary}
              onChange={(e) => updateCurrentRep({ isUltimateBeneficiary: e.target.checked })}
              className="h-4 w-4 text-[#B3A369] focus:ring-[#B3A369] border-gray-300 rounded"
            />
            <label htmlFor="isUltimateBeneficiary" className="ml-2 block text-sm text-gray-900">
              {getTranslation('is_ultimate_beneficiary_label', language)}
            </label>
          </div>
        </div>
      </div>

      {/* Section 2: Personal Data */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {language === 'es' ? '2. Datos Personales' :
           language === 'fr' ? '2. Données personnelles' :
           language === 'de' ? '2. Persönliche Daten' :
           language === 'it' ? '2. Dati personali' :
           language === 'pt' ? '2. Dados pessoais' :
           language === 'nl' ? '2. Persoonlijke gegevens' :
           language === 'ru' ? '2. Личные данные' :
           language === 'ar' ? '2. البيانات الشخصية' :
           '2. Personal Data'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('first_name_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              name="firstName"
              type="text"
              value={currentRep.firstName}
              onChange={(e) => updateCurrentRep({ firstName: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.firstName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.firstName && (
              <p className="mt-1 text-sm text-red-600">{localErrors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('middle_name_label', language)}
            </label>
            <input
              type="text"
              value={currentRep.middleName || ''}
              onChange={(e) => updateCurrentRep({ middleName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('last_name_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              name="lastName"
              type="text"
              value={currentRep.lastName}
              onChange={(e) => updateCurrentRep({ lastName: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.lastName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.lastName && (
              <p className="mt-1 text-sm text-red-600">{localErrors.lastName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('second_last_name_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={currentRep.secondLastName}
              onChange={(e) => updateCurrentRep({ secondLastName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('gender_label', language)} <span className="text-red-500">*</span>
            </label>
            <select
              name="gender"
              value={currentRep.gender}
              onChange={(e) => updateCurrentRep({ gender: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.gender ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">{getTranslation('select_option', language)}</option>
              <option value="MALE">{getTranslation('gender_male', language)}</option>
              <option value="FEMALE">{getTranslation('gender_female', language)}</option>
            </select>
            {localErrors.gender && (
              <p className="mt-1 text-sm text-red-600">{localErrors.gender}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('birth_date_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              name="birthDate"
              type="date"
              value={currentRep.birthDate}
              onChange={(e) => updateCurrentRep({ birthDate: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.birthDate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.birthDate && (
              <p className="mt-1 text-sm text-red-600">{localErrors.birthDate}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <CountrySelector
            value={currentRep.birthCountry}
            onChange={(code) => updateCurrentRep({ birthCountry: code })}
            language={language}
            label={getTranslation('birth_country_label', language)}
            required={true}
            error={localErrors.birthCountry}
          />

          <CountrySelector
            value={currentRep.nationality}
            onChange={(code) => updateCurrentRep({ nationality: code })}
            language={language}
            label={getTranslation('nationality_label', language)}
            required={true}
            error={localErrors.nationality}
          />

          <CountrySelector
            value={currentRep.citizenship}
            onChange={(code) => updateCurrentRep({ citizenship: code })}
            language={language}
            label={getTranslation('citizenship_label', language)}
            required={true}
            error={localErrors.citizenship}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <CountrySelector
            value={currentRep.residenceCountry}
            onChange={(code) => updateCurrentRep({ residenceCountry: code })}
            language={language}
            label={getTranslation('residence_country_label', language)}
            required={true}
            error={localErrors.residenceCountry}
          />

          <CountrySelector
            value={currentRep.secondNationality || ''}
            onChange={(code) => updateCurrentRep({ secondNationality: code })}
            language={language}
            label={getTranslation('second_nationality_label', language)}
          />
        </div>
      </div>

      {/* Section 3: Document */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {language === 'es' ? '3. Documento de Identidad' :
           language === 'fr' ? '3. Document d\'identité' :
           language === 'de' ? '3. Ausweisdokument' :
           language === 'it' ? '3. Documento d\'identità' :
           language === 'pt' ? '3. Documento de identidade' :
           language === 'nl' ? '3. Identiteitsbewijs' :
           language === 'ru' ? '3. Документ, удостоверяющий личность' :
           language === 'ar' ? '3. وثيقة الهوية' :
           '3. Identity Document'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('document_type_label', language)} <span className="text-red-500">*</span>
            </label>
            <select
              name="documentType"
              value={currentRep.documentType}
              onChange={(e) => updateCurrentRep({ documentType: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.documentType ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">{getTranslation('select_option', language)}</option>
              <option value="ID_CARD">{getTranslation('document_type_id_card', language)}</option>
              <option value="PASSPORT">{getTranslation('document_type_passport', language)}</option>
              <option value="DRIVING_LICENSE">{getTranslation('document_type_driving_license', language)}</option>
            </select>
            {localErrors.documentType && (
              <p className="mt-1 text-sm text-red-600">{localErrors.documentType}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('document_number_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              name="documentNumber"
              type="text"
              value={currentRep.documentNumber}
              onChange={(e) => updateCurrentRep({ documentNumber: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.documentNumber ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.documentNumber && (
              <p className="mt-1 text-sm text-red-600">{localErrors.documentNumber}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <CountrySelector
            value={currentRep.documentCountry}
            onChange={(code) => updateCurrentRep({ documentCountry: code })}
            language={language}
            label={getTranslation('document_country_label', language)}
            required={true}
            error={localErrors.documentCountry}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('document_expiry_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              name="documentExpiry"
              type="date"
              value={currentRep.documentExpiry}
              onChange={(e) => updateCurrentRep({ documentExpiry: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.documentExpiry ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.documentExpiry && (
              <p className="mt-1 text-sm text-red-600">{localErrors.documentExpiry}</p>
            )}
          </div>
        </div>
      </div>

      {/* Section 4: Contact */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {language === 'es' ? '4. Contacto' :
           language === 'fr' ? '4. Contact' :
           language === 'de' ? '4. Kontakt' :
           language === 'it' ? '4. Contatto' :
           language === 'pt' ? '4. Contato' :
           language === 'nl' ? '4. Contact' :
           language === 'ru' ? '4. Контакт' :
           language === 'ar' ? '4. الاتصال' :
           '4. Contact'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('email_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              name="email"
              type="email"
              value={currentRep.email}
              onChange={(e) => updateCurrentRep({ email: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.email && (
              <p className="mt-1 text-sm text-red-600">{localErrors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('phone_number_label', language)} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <select
                name="phoneCountryCode"
                value={currentRep.phoneCountryCode}
                onChange={(e) => updateCurrentRep({ phoneCountryCode: e.target.value })}
                className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                  localErrors.phoneCountryCode ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Code</option>
                {PHONE_COUNTRY_CODES.map((item) => (
                  <option key={item.iso2} value={item.code}>
                    {item.code} - {item.country}
                  </option>
                ))}
              </select>
              <input
                name="phoneNumber"
                type="tel"
                value={currentRep.phoneNumber}
                onChange={(e) => updateCurrentRep({ phoneNumber: e.target.value.replace(/\D/g, '') })}
                className={`col-span-2 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                  localErrors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {(localErrors.phoneCountryCode || localErrors.phoneNumber) && (
              <p className="mt-1 text-sm text-red-600">
                {localErrors.phoneCountryCode || localErrors.phoneNumber}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section 5: Address */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {language === 'es' ? '5. Dirección' :
           language === 'fr' ? '5. Adresse' :
           language === 'de' ? '5. Adresse' :
           language === 'it' ? '5. Indirizzo' :
           language === 'pt' ? '5. Endereço' :
           language === 'nl' ? '5. Adres' :
           language === 'ru' ? '5. Адрес' :
           language === 'ar' ? '5. العنوان' :
           '5. Address'}
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('address_line1_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              name="addressLine1"
              type="text"
              value={currentRep.addressLine1}
              onChange={(e) => updateCurrentRep({ addressLine1: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.addressLine1 ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.addressLine1 && (
              <p className="mt-1 text-sm text-red-600">{localErrors.addressLine1}</p>
            )}
          </div>

          {/* Country first to determine state selector type */}
          <CountrySelector
            value={currentRep.country}
            onChange={(code) => updateCurrentRep({ country: code, state: '' })} // Clear state when country changes
            language={language}
            label={getTranslation('country_label', language)}
            required={true}
            error={localErrors.country}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {getTranslation('city_label', language)} <span className="text-red-500">*</span>
              </label>
              <input
                name="city"
                type="text"
                value={currentRep.city}
                onChange={(e) => updateCurrentRep({ city: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                  localErrors.city ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {localErrors.city && (
                <p className="mt-1 text-sm text-red-600">{localErrors.city}</p>
              )}
            </div>

            {/* State/Province - Conditional: US State Selector or Text Input */}
            {currentRep.country === 'US' ? (
              <USStateSelector
                value={currentRep.state}
                onChange={(code) => updateCurrentRep({ state: code })}
                language={language}
                label={language === 'es'
                  ? 'Estado (código ISO-2)'
                  : language === 'fr'
                  ? 'État (code ISO-2)'
                  : language === 'de'
                  ? 'Staat (ISO-2-Code)'
                  : language === 'it'
                  ? 'Stato (codice ISO-2)'
                  : language === 'pt'
                  ? 'Estado (código ISO-2)'
                  : language === 'nl'
                  ? 'Staat (ISO-2-code)'
                  : language === 'ru'
                  ? 'Штат (код ISO-2)'
                  : language === 'ar'
                  ? 'الولاية (رمز ISO-2)'
                  : 'State (ISO-2 code)'
                }
                required={true}
                error={localErrors.state}
              />
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {getTranslation('state_label', language)} <span className="text-red-500">*</span>
                </label>
                <input
                  name="state"
                  type="text"
                  value={currentRep.state}
                  onChange={(e) => updateCurrentRep({ state: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                    localErrors.state ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {localErrors.state && (
                  <p className="mt-1 text-sm text-red-600">{localErrors.state}</p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('postal_code_label', language)} <span className="text-red-500">*</span>
            </label>
            <input
              name="postalCode"
              type="text"
              value={currentRep.postalCode}
              onChange={(e) => updateCurrentRep({ postalCode: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.postalCode ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.postalCode && (
              <p className="mt-1 text-sm text-red-600">{localErrors.postalCode}</p>
            )}
          </div>
        </div>
      </div>

      {/* Section 6: Employment */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {language === 'es' ? '6. Información Laboral y Fiscal' :
           language === 'fr' ? '6. Informations professionnelles et fiscales' :
           language === 'de' ? '6. Beschäftigungs- und Steuerinformationen' :
           language === 'it' ? '6. Informazioni lavorative e fiscali' :
           language === 'pt' ? '6. Informações trabalhistas e fiscais' :
           language === 'nl' ? '6. Werk- en belastinginformatie' :
           language === 'ru' ? '6. Информация о занятости и налогах' :
           language === 'ar' ? '6. معلومات التوظيف والضرائب' :
           '6. Employment and Tax Information'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('employment_status_label', language)} <span className="text-red-500">*</span>
            </label>
            <select
              name="employmentStatus"
              value={currentRep.employmentStatus}
              onChange={(e) => updateCurrentRep({ employmentStatus: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.employmentStatus ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">{getTranslation('select_option', language)}</option>
              <option value="EMPLOYED">{getTranslation('employment_employed', language)}</option>
              <option value="SELF_EMPLOYED">{getTranslation('employment_self_employed', language)}</option>
              <option value="UNEMPLOYED">{getTranslation('employment_unemployed', language)}</option>
              <option value="RETIRED">{getTranslation('employment_retired', language)}</option>
              <option value="STUDENT">{getTranslation('employment_student', language)}</option>
            </select>
            {localErrors.employmentStatus && (
              <p className="mt-1 text-sm text-red-600">{localErrors.employmentStatus}</p>
            )}
          </div>

          <SOCSelector
            value={currentRep.occupation}
            onChange={(code) => updateCurrentRep({ occupation: code })}
            language={language}
            label={getTranslation('occupation_label', language)}
            required={true}
            error={localErrors.occupation}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('employer_name_label', language)}
              {(currentRep.employmentStatus === 'EMPLOYED' || currentRep.employmentStatus === 'SELF_EMPLOYED') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <input
              name="employerName"
              type="text"
              value={currentRep.employerName}
              onChange={(e) => updateCurrentRep({ employerName: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.employerName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.employerName && (
              <p className="mt-1 text-sm text-red-600">{localErrors.employerName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('job_title_label', language)}
              {(currentRep.employmentStatus === 'EMPLOYED' || currentRep.employmentStatus === 'SELF_EMPLOYED') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <input
              name="jobTitle"
              type="text"
              value={currentRep.jobTitle}
              onChange={(e) => updateCurrentRep({ jobTitle: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.jobTitle ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.jobTitle && (
              <p className="mt-1 text-sm text-red-600">{localErrors.jobTitle}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <CountrySelector
            value={currentRep.taxCountry}
            onChange={(code) => updateCurrentRep({ taxCountry: code })}
            language={language}
            label={getTranslation('tax_country_label', language)}
            required={true}
            error={localErrors.taxCountry}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('tax_number_label', language)}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              name="taxNumber"
              type="text"
              value={currentRep.taxNumber || ''}
              onChange={(e) => updateCurrentRep({ taxNumber: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                localErrors.taxNumber ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {localErrors.taxNumber && (
              <p className="mt-1 text-sm text-red-600">{localErrors.taxNumber}</p>
            )}
          </div>
        </div>

        {/* PEP Status */}
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <label className="block text-sm font-medium text-gray-700">
              {getTranslation('is_pep_label', language)}
            </label>
            <div className="relative group">
              <svg
                className="w-4 h-4 text-gray-400 cursor-help"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                {getTranslation('is_pep_tooltip', language)}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>

          <div className="mt-3 flex space-x-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="isPep"
                checked={currentRep.isPep === true}
                onChange={() => updateCurrentRep({ isPep: true })}
                className="w-4 h-4 text-[#B3A369] border-gray-300 focus:ring-[#B3A369]"
              />
              <span className="ml-2 text-sm text-gray-700">
                {getTranslation('yes', language)}
              </span>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="isPep"
                checked={currentRep.isPep === false}
                onChange={() => updateCurrentRep({ isPep: false })}
                className="w-4 h-4 text-[#B3A369] border-gray-300 focus:ring-[#B3A369]"
              />
              <span className="ml-2 text-sm text-gray-700">
                {getTranslation('no', language)}
              </span>
            </label>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslation('income_source_label', language)}
          </label>
          <input
            type="text"
            value={currentRep.incomeSource || ''}
            onChange={(e) => updateCurrentRep({ incomeSource: e.target.value })}
            placeholder="SALARY, BUSINESS, INVESTMENTS, etc."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleCancel}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {language === 'es' ? 'Cancelar' :
           language === 'fr' ? 'Annuler' :
           language === 'de' ? 'Abbrechen' :
           language === 'it' ? 'Annulla' :
           language === 'pt' ? 'Cancelar' :
           language === 'nl' ? 'Annuleren' :
           language === 'ru' ? 'Отмена' :
           language === 'ar' ? 'إلغاء' :
           'Cancel'}
        </button>

        <button
          type="button"
          onClick={handleSave}
          className="px-6 py-2 bg-[#B3A369] text-white rounded-lg hover:bg-[#9A8B57] transition-colors"
        >
          {getTranslation('save_button', language)}
        </button>
      </div>
    </div>
  );
};

export default Step5_RepresentativeForm;
